import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Object3D, Vector2Tuple } from 'three';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import {
  getOrientedBox2Corners,
  NavMeshHelper,
  RectControls,
  RectControlsProps,
  useDollhouse,
  useSelection,
} from '@spazious/dollhouse-engine';
import {
  FArrangementBase,
  FCompositionNode,
  FNode,
  FNodeTypesEnum,
  FurnitureNode,
  getNodeFromObject,
  isFArrangementNode,
} from '@spazious/dollhouse-fsetup';
import { NavMeshQuery, resizePolygonInNavMesh } from '@spazious/dollhouse-navmesh';
import { useStaging, useStore } from '@spazious/dollhouse-store';
import { ControlsModeEnum } from '@spazious/dollhouse-types';
import { useStyleVars } from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

import { DollhouseMessage, MainViewMessage } from '../../core/EventManager.js';
import {
  useNodeMetadata,
  useNodesPlacementValidator,
  useRestrictedArea,
  useUnitConverter,
} from '../../hooks/index.js';
import { useEvents } from '../EventManager/index.js';

export interface FArrangementRectControlsProps extends RectControlsProps {
  node: FCompositionNode;
  selected: Object3D[] | null;
  resizeWithinNavMesh?: boolean;
}

const resizeNode = (
  node: FCompositionNode,
  center: [number, number],
  size: [number, number],
  theta: number,
  navMeshQuery?: NavMeshQuery
) => {
  const instance = (node as any).instance as any;
  let nodeSize = instance.getValue('size');
  let minimumSize = instance.getMinimumSize();
  return resizeBox(node.position, nodeSize, center, size, minimumSize, theta, navMeshQuery);
};

const resizeBox = (
  center: [number, number],
  size: [number, number],
  targetCenter: [number, number],
  targetSize: [number, number],
  minSize: [number, number],
  theta: number,
  navMeshQuery?: NavMeshQuery
): {
  corners: [number, number][];
  center: [number, number];
  size: [number, number];
} => {
  if (minSize[0] > targetSize[0]) {
    let ratio = (minSize[0] - targetSize[0]) / (size[0] - targetSize[0]);
    targetCenter[0] = targetCenter[0] + (center[0] - targetCenter[0]) * ratio;
    targetSize[0] = minSize[0];
  }

  if (minSize[1] > targetSize[1]) {
    let ratio = (minSize[1] - targetSize[1]) / (size[1] - targetSize[1]);
    targetCenter[1] = targetCenter[1] + (center[1] - targetCenter[1]) * ratio;
    targetSize[1] = minSize[1];
  }

  let targetCorners = getOrientedBox2Corners(targetCenter, targetSize, theta);
  if (navMeshQuery) {
    const startCorners = getOrientedBox2Corners(center, size, theta);
    targetCorners = resizePolygonInNavMesh(startCorners, targetCorners, navMeshQuery).corners;
  }

  const [p0, , p2] = targetCorners;
  return {
    corners: targetCorners,
    center: [(p0[0] + p2[0]) / 2, (p0[1] + p2[1]) / 2], // mid point of diagonal p0-p2
    size: targetSize,
  };
};

export const FArrangementRectControls: React.FC<FArrangementRectControlsProps> = ({
  node,
  selected,
  resizeWithinNavMesh = false,
  ...restProps
}) => {
  const { staging, createAction } = useStaging();
  const events = useEvents();
  const { select } = useSelection();
  const { hasInvisiblenodes, confirmReset, resetInvisiblenodes, deletionsCount } =
    useNodeMetadata();
  const { isPolygonInRestrictedArea, confirm } = useRestrictedArea();
  const checkRestrictedAreas = useStore(({ settings }) => settings.checkRestrictedAreas);
  const navMesh = useDollhouse(({ world }) => world.navMesh);
  const navMeshQuery = useDollhouse(({ world }) => world.navMeshQuery);
  const [navMeshColor] = useStyleVars('--spz-sys-color-success');
  const [showNavMesh, setShowNavMesh] = useState(false);
  const { displayUnit } = useUnitConverter();
  const { validator } = useNodesPlacementValidator([node]);
  const _navMeshQuery = resizeWithinNavMesh ? navMeshQuery : undefined;

  const labelFormatter = (value: string | number) => {
    const showUnit = true;
    return displayUnit(value, showUnit);
  };
  const [stopResize, setStopResize] = useState(false);
  const childrenNodes = useMemo(() => {
    const furnitureNodes: FurnitureNode[] = [];
    node?.traverse((node) => {
      if (node.type === FNodeTypesEnum.Furniture) {
        furnitureNodes.push(node);
      }
    });

    return furnitureNodes;
  }, [node]);

  const onConfirmMessage = (message: MainViewMessage) => {
    if (message === MainViewMessage.RestrictedAreaActionRevert) {
      staging.abort();
    } else if (message === MainViewMessage.RestrictedAreaActionIgnore) {
      staging.commit(CommanderActionTypesEnum.Param);
      if (selected) {
        setTimeout(() => select(selected), 100);
      }
    }
  };

  useEffect(() => {
    events.emit(
      'dollhouseMessage',
      DollhouseMessage.GetDeletionsCount,
      deletionsCount(childrenNodes)
    );
  }, [deletionsCount, events, node, hasInvisiblenodes, childrenNodes]);

  useEffect(() => {
    if (stopResize) {
      confirmReset((message: MainViewMessage) => {
        if (message === MainViewMessage.InvisibleNodesResetConfirm) {
          resetInvisiblenodes(childrenNodes);
        }
      });
    }
  }, [childrenNodes, confirmReset, resetInvisiblenodes, stopResize]);

  const handleChange = useEventCallback<PickProp<RectControlsProps, 'onChange'>>((evt) => {
    const { center: targetCenter, size: targetSize, theta } = evt;
    if (hasInvisiblenodes(childrenNodes)) {
      setStopResize(true);
      return;
    }

    if (evt.originalEvent?.type === 'pointerdown') {
      staging.prepare();
      setShowNavMesh(true);
    } else if (evt.originalEvent?.type === 'pointermove') {
      const { center, size } = resizeNode(node, targetCenter, targetSize, theta, _navMeshQuery);

      staging.add(createAction('update', node.selector!, 'attrs.size', size));
      staging.add(createAction('update', node.selector!, 'position', center));
      staging.add(createAction('update', node.selector!, 'rotation', theta));
    } else if (evt.originalEvent?.type === 'pointerup') {
      setShowNavMesh(false);
      const success = validator();
      if (success) {
        const { corners } = resizeNode(node, targetCenter, targetSize, theta, _navMeshQuery);

        if (checkRestrictedAreas && isPolygonInRestrictedArea([corners as Vector2Tuple[]])) {
          confirm(onConfirmMessage);
        } else {
          staging.commit(CommanderActionTypesEnum.Param);
          if (selected) {
            setTimeout(() => select(selected), 100);
          }
        }
      } else {
        // some furnitures are outside navmesh
        staging.abort();
      }
    }
  });

  useEffect(() => {
    const instance = (node as any).instance as FArrangementBase<any, any>;
    if (!instance) return;

    const handleMinSizeUpdate = () => {
      const minSize = instance.getMinimumSize();
      let size = instance.getValue('size') as Vector2Tuple;
      if (size[0] < minSize[0] || size[1] < minSize[1]) {
        const newSize = [Math.max(size[0], minSize[0]), Math.max(size[1], minSize[1])];
        staging.add(createAction('update', node.selector!, 'attrs.size', newSize));
        staging.commit(CommanderActionTypesEnum.Param);
      }
    };

    instance.events.on('minsizeupdate', handleMinSizeUpdate);

    return () => {
      instance.events.off('minsizeupdate', handleMinSizeUpdate);
    };
  }, [createAction, node, staging]);

  const instance = (node as any).instance as any;
  let size = instance.getValue('size');

  return (
    <group>
      <RectControls
        center={node.position}
        labelFormatter={labelFormatter}
        navMeshQuery={_navMeshQuery}
        size={size}
        theta={node.rotation}
        onChange={handleChange}
        {...restProps}
      />
      {navMesh && showNavMesh && <NavMeshHelper color={navMeshColor} navMesh={navMesh} />}
    </group>
  );
};

export const FArrangementControls: React.FC = () => {
  const [activeNode, setActiveNode] = useState<FCompositionNode | null>(null);
  const selectedRef = useRef<Object3D[] | null>(null);
  const { selected } = useSelection();
  const lookMode = useStore(({ controls }) => controls === ControlsModeEnum.Walk);

  useEffect(() => {
    if (selected.length === 1) {
      selectedRef.current = selected;
      const node = getNodeFromObject(selected[0]) as any as FNode;
      if (node && isFArrangementNode(node) && node.selector) {
        setActiveNode(node);
      } else if (
        node?.type === FNodeTypesEnum.Furniture &&
        node.originator &&
        isFArrangementNode(node.originator)
      ) {
        setActiveNode(node.originator);
      }
    } else if (selected.length === 0) {
      setActiveNode(null);
    }
  }, [selected]);

  const userData = useMemo(() => {
    // TODO: this controls: true has been added as a workaround for SPZD-542, but controls flag shouldn't be used and it should be deleted
    return { node: activeNode!, controls: true };
  }, [activeNode]);

  return activeNode && !lookMode ? (
    <group>
      <FArrangementRectControls
        node={activeNode}
        selected={selectedRef.current}
        userData={userData as any}
      />
    </group>
  ) : null;
};
